import type { AgentBarScenario } from './agentBarScenarios';

export const AGENT_BAR_FEEDBACK_STORAGE_KEY = 'proposal-builder.agent-bar-feedback.v1';

export interface AgentBarFeedbackComment {
  id: string;
  scope: 'global' | string;
  body: string;
  createdAt: string;
}

interface StorageReader {
  getItem: (key: string) => string | null;
}

interface StorageWriter {
  setItem: (key: string, value: string) => void;
}

function isFeedbackComment(value: unknown): value is AgentBarFeedbackComment {
  if (!value || typeof value !== 'object') return false;
  const comment = value as Partial<AgentBarFeedbackComment>;
  return (
    typeof comment.id === 'string' &&
    typeof comment.scope === 'string' &&
    typeof comment.body === 'string' &&
    typeof comment.createdAt === 'string'
  );
}

export function readAgentBarFeedback(storage: StorageReader): AgentBarFeedbackComment[] {
  try {
    const raw = storage.getItem(AGENT_BAR_FEEDBACK_STORAGE_KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter(isFeedbackComment) : [];
  } catch {
    return [];
  }
}

export function saveAgentBarFeedback(storage: StorageWriter, comments: AgentBarFeedbackComment[]): void {
  storage.setItem(AGENT_BAR_FEEDBACK_STORAGE_KEY, JSON.stringify(comments));
}

export function feedbackVisibleForScenario(
  comments: AgentBarFeedbackComment[],
  scenarioId: string | null,
): AgentBarFeedbackComment[] {
  return comments.filter((comment) => comment.scope === 'global' || comment.scope === scenarioId);
}

export function formatAgentBarFeedback(
  comments: AgentBarFeedbackComment[],
  scenarios: AgentBarScenario[],
): string {
  const lines = ['# AgentBar review', ''];
  const appendSection = (title: string, scoped: AgentBarFeedbackComment[]) => {
    if (scoped.length === 0) return;
    lines.push(`## ${title}`, '');
    scoped.forEach((comment) => lines.push(`- ${comment.body}`));
    lines.push('');
  };

  appendSection('Global fixes', comments.filter((comment) => comment.scope === 'global'));
  scenarios.forEach((scenario) => {
    appendSection(
      scenario.label,
      comments.filter((comment) => comment.scope === scenario.id),
    );
  });

  const scenarioIds = new Set(scenarios.map((scenario) => scenario.id));
  appendSection(
    'Unmatched states',
    comments.filter((comment) => comment.scope !== 'global' && !scenarioIds.has(comment.scope)),
  );

  if (comments.length === 0) lines.push('No comments yet.', '');
  return lines.join('\n').trimEnd();
}
